import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';

import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { useWebp } from '@r1-frontend/shared/hooks/useWebp';

import { getCityInfo } from '~/src/api/content/geography/get-city-info';
import { staticImagePath } from '~/src/features/mania/data';
import LogoDefault from '~/static/images/menu/logo_default.svg';

import * as ST from './styled';

interface IProps {
    city: ICurrentCity,
    providerId: number,
    dealersReward: number,
    openCitySelect: () => void,
}

const Header = ({ city, providerId, dealersReward, openCitySelect }: IProps): JSX.Element => {
    const { logoFull, name } = city;
    const [logoImg, updateLogoImg] = useState(logoFull);

    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);
    const isSMobile = useMatchMedia(`(max-width: ${wideBreakpoints.sMobile})`);

    const currentLogoImg = logoImg || LogoDefault;
    const currentBgName = isMobile ? 'head_bg_mobile' : 'head_bg';
    const currentBgExt = useWebp() ? 'webp' : 'jpg';
    const currentBtnSize = isSMobile ? btnSizes.SMALL : btnSizes.LARGE;

    const getLogoData = useCallback(async() => {
        const resp = await getCityInfo({ providerId, filter: ['logo'], active: false });

        if (resp.ok && resp.data?.logoFull) {
            updateLogoImg(`${resp.data.logoFull}`);
        }
    }, [providerId]);

    useEffect(() => {
        getLogoData();
    }, [getLogoData]);

    return (
        <ST.HeaderWrapper mainBg={`${staticImagePath}/header/${currentBgName}.${currentBgExt}`} data-test='header-wrapper'>
            <ST.HeaderContainer>
                <ST.ContentBlock>
                    <ST.CityDataBlock>
                        <Link href={'/'} passHref>
                            <ST.LogoBlock>
                                <ST.Logo
                                    src={currentLogoImg}
                                    alt='Логотип Дом.ру'
                                    data-test='mania-city-logo'
                                />
                            </ST.LogoBlock>
                        </Link>

                        <ST.CitySelectionBlock
                            onClick={openCitySelect}
                            data-test='mania-city-select'
                        >
                            {name}
                        </ST.CitySelectionBlock>
                    </ST.CityDataBlock>
                    <ST.Title>Зарабатывайте с&nbsp;Дом.ру</ST.Title>
                    <ST.Description>
                        Заплатим до<ST.RewardSum>{` ${dealersReward.toLocaleString('ru-RU')} ₽`}</ST.RewardSum> за каждого знакомого,
                        который подключит и&nbsp;оплатит услуги Дом.ру по вашей рекомендации.
                    </ST.Description>
                    <ST.ActionsBlock>
                        <ST.Action
                            type={btnTypes.ACCENT}
                            size={currentBtnSize}
                            text={'Зарегистрироваться'}
                            href={'https://dealers.dom.ru/register'}
                            target='_blank'
                        />
                        <ST.Action
                            type={btnTypes.SECONDARY}
                            size={currentBtnSize}
                            text={'Войти'}
                            href={'https://dealers.dom.ru'}
                            target='_blank'
                        />
                    </ST.ActionsBlock>
                </ST.ContentBlock>
                <ST.ImageBlock>
                    <WebpImage urlWebp={`${staticImagePath}/header/head_img.webp`}>
                        <ST.HeaderImage
                            src={`${staticImagePath}/header/head_img.png`}
                            alt='Изображения двух телефонов с логотипом Дом.ру'
                        />
                    </WebpImage>
                </ST.ImageBlock>
            </ST.HeaderContainer>
        </ST.HeaderWrapper>
    );
};

export default Header;
